<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <div class="header offers-form-header">
            <div>
              <h2>Edit Offer</h2>
              <p class="text-muted mb-0">Update offer content, status, and country visibility.</p>
            </div>
          </div>

          <div class="body pt-0">
            <form class="offers-form" @submit.prevent="updateOffer">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group offer-field">
                    <label class="offer-label">Business</label>
                    <multiselect
                      v-model="selectedBusiness"
                      :options="businessOptions"
                      class="offer-control"
                      :show-labels="false"
                      label="name"
                      track-by="businessid"
                      placeholder="Business"
                      :disabled="true"
                    ></multiselect>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group offer-field">
                    <label class="offer-label">Offer Type</label>
                    <select v-model="form.offer_type" class="form-control offer-control" required>
                      <option disabled value="">Select offer type</option>
                      <option v-for="t in offerTypeOptions" :key="t" :value="t">{{ t }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group offer-field">
                    <label class="offer-label" for="offerImageEdit">Offer Image</label>
                    <div class="offer-upload-control" :class="{ 'is-uploading': isImageUploading }">
                      <input
                        type="file"
                        class="offer-upload-input"
                        id="offerImageEdit"
                        accept="image/*"
                        @change="onOfferImageSelected"
                      />
                      <label class="offer-upload-label" for="offerImageEdit">
                        <span class="offer-upload-text">
                          {{ offerImageFileName || "Choose image" }}
                        </span>
                        <span class="offer-upload-button">
                          {{ isImageUploading ? "Uploading..." : "Browse" }}
                        </span>
                      </label>
                    </div>
                    <small class="offer-help-text">PNG/JPG/WebP, max 5MB</small>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group offer-field">
                    <label class="offer-label">Countries</label>
                    <multiselect
                      v-model="selectedCountries"
                      :options="countryOptions"
                      class="offer-control"
                      :show-labels="false"
                      :multiple="true"
                      :close-on-select="false"
                      :clear-on-select="false"
                      :limit="2"
                      :limit-text="countryLimitText"
                      label="countryname"
                      track-by="countryid"
                      placeholder="Select source countries"
                    ></multiselect>
                    <small class="offer-help-text">
                      You can assign the same offer to multiple countries.
                    </small>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group offer-field">
                    <label class="offer-label">Status</label>
                    <select v-model="form.status" class="form-control offer-control">
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                      <option value="expired">expired</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group offer-field">
                    <label class="offer-label">Image Preview</label>
                    <div class="preview-wrapper">
                      <img
                        v-if="offerImagePreviewUrl"
                        :src="offerImagePreviewUrl"
                        class="offer-preview"
                        alt="Offer preview"
                        @error="imageError = true"
                      />
                      <div v-if="!offerImagePreviewUrl || imageError" class="preview-placeholder">
                        No image preview
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="offer-actions d-flex flex-column flex-md-row justify-content-center">
                <b-button variant="secondary btn-mw" size="lg" @click="$router.push('/offers/list')">
                  Cancel
                </b-button>
                <b-button type="submit" variant="primary ml-md-1 mt-2 mt-md-0 btn-mw" size="lg">
                  Update Offer
                </b-button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <main-loader :loading="isLoading"></main-loader>
  </div>
</template>

<script>
import { GET_OFFER_DETAILS, UPDATE_OFFER } from "@/store/actions.type";
import { ArchBaseService } from "@/common/api.service";
import { normalizeVerifiedBusinessesList } from "@/utils/offersBusinessList";

const LoaderComponent = require("@/components/admin/loader/LoaderComponent.vue").default;
const BreadCrumb = require("@/components/BreadCrumb.vue").default;

export default {
  name: "EditOfferComponent",
  components: {
    "main-loader": LoaderComponent,
    "bread-crumb": BreadCrumb,
    Multiselect: () => import("vue-multiselect"),
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Offers", links: "/offers/list" },
        { title: "Edit Offer", links: "" },
      ],
      isLoading: false,
      isImageUploading: false,
      imageError: false,
      offerImagePreviewUrl: "",
      offerImageFileName: "",
      businessOptions: [],
      countryOptions: [],
      selectedBusiness: null,
      selectedCountries: [],
      offerTypeOptions: ["Remmit care", "Business"],
      form: {
        id: "",
        business_id: "",
        offer_type: "",
        offer_image: "",
        country_ids: [],
        status: "active",
      },
    };
  },
  watch: {
    "form.offer_image"() {
      this.imageError = false;
    },
  },
  mounted() {
    this.initPage();
  },
  methods: {
    countryLimitText(count) {
      return `+${count} more`;
    },
    async onOfferImageSelected(e) {
      if (!e?.target?.files?.length) return;
      const file = e.target.files[0];
      if (!file) return;

      if (!file.type?.startsWith("image/")) {
        this.$toast.error("Please select a valid image file.", { position: "top-right" });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$toast.error("Image must be 5MB or smaller.", { position: "top-right" });
        return;
      }

      this.offerImageFileName = file.name;
      this.isImageUploading = true;
      this.imageError = false;
      try {
        const formData = new FormData();
        formData.append("file", file);

        const resp = await this.$http.post("offers/upload-image", formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
            "Content-Type": "multipart/form-data",
          },
        });

        const details = resp?.data?.details;
        this.form.offer_image = details?.url || "";
        this.offerImagePreviewUrl = details?.sas_url || details?.url || "";
        if (!this.form.offer_image) {
          this.$toast.error("Failed to upload image.", { position: "top-right" });
        }
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Failed to upload image.", {
          position: "top-right",
        });
      } finally {
        this.isImageUploading = false;
      }
    },
    async initPage() {
      this.isLoading = true;
      try {
        ArchBaseService.init();
        const [businessResp, countryResp] = await Promise.all([
          ArchBaseService.get("offers/verified-businesses"),
          ArchBaseService.get("offers/source-countries"),
        ]);

        this.businessOptions = normalizeVerifiedBusinessesList(businessResp?.data?.details || []);
        this.countryOptions = countryResp?.data?.details || [];

        await this.loadOffer();
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Failed to initialize page.", {
          position: "top-right",
        });
        this.$router.push("/offers/list");
      } finally {
        this.isLoading = false;
      }
    },
    async loadOffer() {
      try {
        const response = await this.$store.dispatch(GET_OFFER_DETAILS, this.$route.params.id);
        const detail = response?.data?.details;
        if (!detail) {
          this.$toast.error("Offer not found.", { position: "top-right" });
          this.$router.push("/offers/list");
          return;
        }
        this.form = {
          id: detail.id,
          business_id: detail.business_id || "",
          offer_type: detail.offer_type || "",
          offer_image: detail.offer_image || "",
          country_ids: Array.isArray(detail.country_ids)
            ? detail.country_ids
            : detail.country_id
              ? [detail.country_id]
              : [],
          status: detail.status || "active",
        };
        this.offerImagePreviewUrl = this.form.offer_image || "";
        this.offerImageFileName = this.form.offer_image ? "Current image uploaded" : "";

        this.selectedBusiness =
          this.businessOptions.find((b) => b.businessid === this.form.business_id) || null;
        this.selectedCountries = this.countryOptions.filter((country) =>
          this.form.country_ids.includes(country.countryid),
        );
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Failed to load offer.", {
          position: "top-right",
        });
        this.$router.push("/offers/list");
      }
    },
    async updateOffer() {
      this.isLoading = true;
      try {
        if (!this.form.offer_image) {
          this.$toast.error("Please upload an offer image.", { position: "top-right" });
          return;
        }
        if (!this.selectedCountries.length) {
          this.$toast.error("Please select at least one source country.", { position: "top-right" });
          return;
        }
        const payload = {
          offer_type: this.form.offer_type,
          offer_image: this.form.offer_image,
          status: this.form.status,
          country_ids: this.selectedCountries.map((country) => country.countryid),
        };

        const response = await this.$store.dispatch(UPDATE_OFFER, {
          id: this.form.id,
          data: payload,
        });
        if (response?.data?.code) {
          this.$toast.success(response.data.message || "Offer updated successfully.", {
            position: "top-right",
          });
          this.$router.push("/offers/list");
        }
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Failed to update offer.", {
          position: "top-right",
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.offers-form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.offers-form-header p {
  margin-top: 4px;
}

.offers-form {
  padding-top: 8px;
}

.offer-field {
  margin-bottom: 22px;
}

.offer-label {
  display: inline-block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2f3c4d;
}

.offer-help-text {
  display: block;
  margin-top: 8px;
  color: #8a94a6;
  font-size: 12px;
  line-height: 1.45;
}

.offer-control {
  min-height: 46px;
}

.offer-actions {
  margin-top: 6px;
  padding-top: 18px;
  border-top: 1px solid #edf2f7;
}

.offer-upload-control {
  position: relative;
}

.offer-upload-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.offer-upload-label {
  width: 100%;
  min-height: 46px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #dfe5ec;
  border-radius: 4px;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
}

.offer-upload-text {
  flex: 1;
  min-width: 0;
  padding: 11px 14px;
  color: #495057;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.offer-upload-button {
  flex-shrink: 0;
  padding: 11px 18px;
  border-left: 1px solid #dfe5ec;
  background: #f5f7fa;
  color: #495057;
  font-size: 14px;
  font-weight: 500;
}

.offer-upload-control.is-uploading .offer-upload-button {
  color: #6c757d;
}

.preview-wrapper {
  min-height: 180px;
  border: 1px dashed #ced4da;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  overflow: hidden;
}

.offer-preview {
  width: 100%;
  height: 180px;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  padding: 12px;
}

.preview-placeholder {
  color: #6c757d;
  font-size: 13px;
  text-align: center;
}

/deep/ .multiselect {
  min-height: 46px;
}

/deep/ .multiselect__tags,
/deep/ .multiselect__single,
/deep/ .multiselect__input,
/deep/ .multiselect__select {
  font-size: 14px;
}

/deep/ .multiselect__tags {
  min-height: 46px;
  padding: 10px 40px 0 12px;
  border-color: #dfe5ec;
  border-radius: 4px;
  background: #fff;
}

/deep/ .multiselect__single,
/deep/ .multiselect__input {
  margin-bottom: 8px;
}

/deep/ .multiselect__tag {
  margin-bottom: 8px;
  background: rgba(71, 167, 184, 0.12);
  color: #157786;
}

/deep/ .multiselect__tag-icon:after {
  color: #157786;
}

/deep/ .multiselect--active .multiselect__tags,
/deep/ .multiselect__tags:hover,
.offer-control:focus {
  border-color: #47a7b8;
}

@media (max-width: 767px) {
  .offer-actions .btn {
    width: 100%;
  }
}
</style>
